import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import type { ExecuteCommandResponse } from "./useRunAdminCommand";

interface RunAdminCommandResultPopupProps {
  open: boolean;
  onClose: () => void;
  result: ExecuteCommandResponse | null;
  title?: string;
}

function CopyableField({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    const textValue =
      value === null || value === undefined ? "" : String(value);
    navigator.clipboard.writeText(textValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const displayValue =
    value === null || value === undefined ? "(empty)" : String(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
        px: 1.5,
        borderRadius: 1,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            wordBreak: "break-all",
            color:
              value === null || value === undefined
                ? "text.disabled"
                : "text.primary",
          }}
        >
          {displayValue}
        </Typography>
      </Box>
      <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
        <IconButton size="small" onClick={handleCopy} sx={{ ml: 1 }}>
          {copied ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function RunAdminCommandResultPopup({
  open,
  onClose,
  result,
  title = "Command Result",
}: RunAdminCommandResultPopupProps) {
  if (!result) return null;

  const renderResultFields = (data: Record<string, any>, prefix = "") => {
    return Object.entries(data).map(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      // Skip rendering complex nested objects inline - show as JSON instead
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return (
          <Box key={fullKey} sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 1.5 }}
            >
              {key}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                ml: 1.5,
                mr: 1.5,
                mt: 0.5,
                backgroundColor: "#f9f9f9",
              }}
            >
              {renderResultFields(value, fullKey)}
            </Paper>
          </Box>
        );
      }

      // Handle arrays
      if (Array.isArray(value)) {
        return (
          <CopyableField
            key={fullKey}
            label={key}
            value={JSON.stringify(value)}
          />
        );
      }

      // Handle primitive values
      return <CopyableField key={fullKey} label={key} value={value} />;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Alert severity={result.success ? "success" : "error"} sx={{ mb: 2 }}>
          {result.success
            ? "Command executed successfully"
            : result.error || "Command execution failed"}
        </Alert>

        {result.result && (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ px: 1.5 }}>
              Result
            </Typography>
            <Paper variant="outlined" sx={{ mb: 2 }}>
              {renderResultFields(result.result)}
            </Paper>
          </Box>
        )}

        {result.logs && result.logs.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ px: 1.5 }}>
              Logs ({result.logs.length})
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                backgroundColor: "#f5f5f5",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              {result.logs.map((log, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ fontFamily: "monospace", fontSize: "12px", mb: 0.5 }}
                >
                  {log}
                </Typography>
              ))}
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}